import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Download,
  Loader2,
  CheckCircle2,
  BookOpen,
  AlertCircle,
  RotateCcw,
} from "lucide-react";

export default function PdfReaderCanvas({
  pdfBlobUrl,
  documentTitle = "Manual",
  onFinishedReading,
  onDownload,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [maxPageReached, setMaxPageReached] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Inicializar worker y cargar documento con PDF.js
  useEffect(() => {
    let isCancelled = false;

    async function loadPdf() {
      if (!pdfBlobUrl) return;

      setLoading(true);
      setLoadError(null);
      setCurrentPage(1);
      setMaxPageReached(1);

      try {
        const pdfjs = window.pdfjsLib;
        if (!pdfjs) {
          throw new Error("Librería del visor PDF no encontrada en el sistema.");
        }

        // Configurar worker local
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/vendor/pdfjs/pdf.worker.min.js`;

        const loadingTask = pdfjs.getDocument({
          url: pdfBlobUrl,
          cMapUrl: "https://unpkg.com/pdfjs-dist@3.11.174/cmaps/",
          cMapPacked: true,
        });

        const doc = await loadingTask.promise;
        if (!isCancelled) {
          setPdfDoc(doc);
          setNumPages(doc.numPages);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error al cargar PDF con PDF.js:", err);
        if (!isCancelled) {
          setLoadError(err.message || "Error al procesar el archivo PDF.");
          setLoading(false);
        }
      }
    }

    loadPdf();

    return () => {
      isCancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [pdfBlobUrl]);

  // Renderizar la página actual en el canvas
  const renderPage = useCallback(
    async (pageNum, currentScale) => {
      if (!pdfDoc || !canvasRef.current) return;

      try {
        setRendering(true);

        // Cancelar render previo si estaba en curso
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch (e) {
            // ignore
          }
        }

        const page = await pdfDoc.getPage(pageNum);
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Respetar la escala exacta seleccionada por el usuario
        const viewport = page.getViewport({ scale: currentScale });
        const context = canvas.getContext("2d");

        // Soporte para pantallas retina / alta densidad
        const pixelRatio = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        context.scale(pixelRatio, pixelRatio);

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") {
          console.error("Error al renderizar página del PDF:", err);
        }
      } finally {
        setRendering(false);
      }
    },
    [pdfDoc]
  );

  useEffect(() => {
    if (pdfDoc && numPages > 0) {
      renderPage(currentPage, scale);
    }
  }, [pdfDoc, currentPage, scale, renderPage, numPages]);

  // Manejo de cambio de página
  const goToPage = useCallback(
    (page) => {
      const target = Math.max(1, Math.min(numPages, page));
      setCurrentPage(target);
      setMaxPageReached((prev) => Math.max(prev, target));
      if (target === numPages && onFinishedReading) {
        onFinishedReading();
      }
    },
    [numPages, onFinishedReading]
  );

  const handleNextPage = useCallback(() => goToPage(currentPage + 1), [goToPage, currentPage]);
  const handlePrevPage = useCallback(() => goToPage(currentPage - 1), [goToPage, currentPage]);

  // Al cambiar de página, volver siempre al encabezado superior del documento
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  const zoomIn = () => setScale((prev) => Math.min(3.0, +(prev + 0.2).toFixed(1)));
  const zoomOut = () => setScale((prev) => Math.max(0.6, +(prev - 0.2).toFixed(1)));
  const resetZoom = () => setScale(1.0);

  // Escuchar teclado para avanzar/retroceder páginas
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") handleNextPage();
      if (e.key === "ArrowLeft") handlePrevPage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNextPage, handlePrevPage]);

  const readingPct = numPages > 0 ? Math.round((maxPageReached / numPages) * 100) : 0;
  const isComplete = maxPageReached === numPages && numPages > 0;

  if (loading) {
    return (
      <div className="h-full min-h-[460px] flex flex-col items-center justify-center bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
        <div className="text-center">
          <p className="text-base font-semibold text-slate-800">Cargando páginas del manual...</p>
          <p className="text-xs text-slate-400 mt-1">Preparando visor interactivo de alta fidelidad</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-full min-h-[460px] flex flex-col items-center justify-center bg-white rounded-2xl border border-rose-200 p-8 space-y-4 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
          <AlertCircle className="w-6 h-6" />
        </div>
        <div className="max-w-md">
          <h3 className="font-bold text-slate-800">No se pudo renderizar el PDF interactivo</h3>
          <p className="text-xs text-slate-500 mt-1">{loadError}</p>
        </div>
        <div className="flex gap-2">
          {onDownload && (
            <Button onClick={onDownload} variant="outline" size="sm" className="gap-2">
              <Download className="w-4 h-4" />
              Descargar Archivo Directo
            </Button>
          )}
          <Button
            onClick={() => window.open(pdfBlobUrl, "_blank")}
            className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
            size="sm"
          >
            <Maximize2 className="w-4 h-4" />
            Abrir en pestaña nueva
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-xl select-none ${
        isFullscreen ? "fixed inset-0 z-50 rounded-none" : "h-full min-h-[520px]"
      }`}
    >
      {/* Barra de herramientas superior */}
      <div className="bg-slate-950/95 text-slate-200 px-3 sm:px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 backdrop-blur shrink-0">
        {/* Título & Progreso de lectura */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-orange-600/20 text-orange-400 flex items-center justify-center shrink-0">
            <BookOpen className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-100 truncate max-w-[200px] sm:max-w-xs md:max-w-sm">
              {documentTitle}
            </div>
            <div className="text-[10px] text-slate-400 flex items-center gap-1.5">
              <span>{readingPct}% recorrido</span>
              {isComplete && (
                <span className="text-emerald-400 flex items-center gap-0.5 font-medium">
                  <CheckCircle2 className="w-3 h-3" /> Completo
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Controles de Navegación de Páginas */}
        <div className="flex items-center gap-1 bg-slate-900/90 px-2 py-1 rounded-xl border border-slate-800 mx-auto sm:mx-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevPage}
            disabled={currentPage <= 1 || rendering}
            className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30"
            title="Página Anterior (←)"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <span className="text-xs font-bold text-slate-200 px-2 min-w-[75px] text-center">
            {currentPage} <span className="font-normal text-slate-500">/ {numPages}</span>
          </span>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextPage}
            disabled={currentPage >= numPages || rendering}
            className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30"
            title="Página Siguiente (→)"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Controles de Zoom y Acciones */}
        <div className="flex items-center gap-1">
          <div className="hidden sm:flex items-center bg-slate-900/90 rounded-xl border border-slate-800 p-0.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomOut}
              className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
              title="Alejar Zoom (-)"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </Button>
            <span
              onClick={resetZoom}
              className="text-[11px] font-semibold text-slate-400 px-1.5 cursor-pointer hover:text-white"
              title="Restablecer Zoom"
            >
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomIn}
              className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
              title="Acercar Zoom (+)"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </Button>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>

          {onDownload && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDownload}
              className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
              title="Descargar copia del PDF"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Visor Canvas Central con scroll fluido bidireccional */}
      <div
        ref={scrollAreaRef}
        className="flex-1 overflow-auto bg-slate-950 p-4 sm:p-6 relative min-h-[440px] flex flex-col"
        style={{
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {rendering && (
          <div className="sticky top-2 z-10 self-end mb-2 bg-slate-900/90 backdrop-blur px-3 py-1 rounded-full text-[11px] text-orange-400 flex items-center gap-1.5 border border-slate-800 shadow-md">
            <Loader2 className="w-3 h-3 animate-spin" /> Renderizando...
          </div>
        )}

        <div className="m-auto flex flex-col items-center justify-start shrink-0">
          <canvas
            ref={canvasRef}
            className="shadow-2xl rounded-sm bg-white border border-slate-700/50 block"
          />
        </div>
      </div>

      {/* Barra de progreso inferior y atajo para avanzar */}
      <div className="bg-slate-950 px-4 py-2 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400 shrink-0">
        <div className="flex items-center gap-2">
          <span>Progreso de lectura:</span>
          <div className="w-28 sm:w-44 bg-slate-800 rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isComplete ? "bg-emerald-500" : "bg-orange-500"
              }`}
              style={{ width: `${readingPct}%` }}
            />
          </div>
          <span className="font-semibold text-slate-300 text-[11px]">{readingPct}%</span>
        </div>

        <div className="flex items-center gap-2">
          {currentPage < numPages ? (
            <Button
              size="sm"
              onClick={handleNextPage}
              className="h-7 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 gap-1"
            >
              Hoja Siguiente ({currentPage + 1}/{numPages})
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs py-1 px-2.5 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              ¡Última página alcanzada!
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}
